import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Heading, Paragraph, Skeleton } from '@digdir/designsystemet-react';
import { ChatElipsisIcon, ClockDashedIcon, PersonGroupIcon } from '@navikt/aksel-icons';
import { OrganizationLayout } from '../../../components/OrganizationLayout';
import { useGetTrialStatus, useGetSmsQuotaQuery, useGetSeatQuotaQuery } from '../../../hooks/queries';
import type { SeatQuotaStatus, SmsQuotaStatus } from '../../../api/queries';
import { formatNorwegianDate } from '../../../utils/dateFormatters';
import classes from './SubscriptionsPage.module.css';

const WARNING_THRESHOLD = 80;
const QUOTA_EXCEEDED_THRESHOLD = 100;
const QUOTA_DISABLED = 0;

const numberFormat = new Intl.NumberFormat('nb-NO');

type QuotaKind = 'seatQuota' | 'smsQuota';

type QuotaCardProps = {
  kind: QuotaKind;
  icon: typeof PersonGroupIcon;
  quota?: SeatQuotaStatus | SmsQuotaStatus;
  isLoading: boolean;
  isError: boolean;
  /** Hvorfor kvoten ikke vises, når den er av eller ubegrenset */
  inactiveMessage?: string;
};

/** Én kvote: hvor mye som er brukt, hva som er igjen og totalt */
const QuotaCard = ({ kind, icon: Icon, quota, isLoading, isError, inactiveMessage }: QuotaCardProps) => {
  const { t } = useTranslation();
  const headingId = `${kind}-heading`;
  const unit = t(`organizationPage.subscription.${kind}.unit`);

  const body = () => {
    if (isLoading) return <Skeleton variant="rectangle" width="100%" height={120} />;
    if (isError) {
      return (
        <Alert data-color="danger">
          <Paragraph>{t(`subscriptions.${kind}.error`)}</Paragraph>
        </Alert>
      );
    }
    if (!quota) {
      return (
        <Alert data-color="info">
          <Paragraph>{t(`subscriptions.${kind}.noQuota`)}</Paragraph>
        </Alert>
      );
    }
    if (inactiveMessage) {
      return (
        <Alert data-color="info">
          <Paragraph>{inactiveMessage}</Paragraph>
        </Alert>
      );
    }

    const percentage = quota.limit > 0 ? (quota.used / quota.limit) * 100 : 0;
    return (
      <>
        {quota.percentage >= QUOTA_EXCEEDED_THRESHOLD ? (
          <Alert data-color="danger">
            <Paragraph>{t(`subscriptions.${kind}.quotaExceeded`)}</Paragraph>
          </Alert>
        ) : (
          quota.percentage >= WARNING_THRESHOLD && (
            <Alert data-color="warning">
              <Paragraph>{t(`subscriptions.${kind}.warningPercentage`, { percentage: quota.percentage })}</Paragraph>
            </Alert>
          )
        )}
        <div className={classes.progress}>
          <span aria-hidden="true" className={classes.track}>
            <span className={classes.fill} style={{ width: `${Math.min(100, percentage)}%` }} />
          </span>
          <Paragraph data-size="sm">
            {t('organizationPage.subscription.usedLine', {
              used: numberFormat.format(quota.used),
              total: numberFormat.format(quota.limit),
              remaining: numberFormat.format(quota.remaining),
              unit,
            })}
          </Paragraph>
        </div>
        <dl className={classes.stats}>
          <div>
            <dt>{t('organizationPage.subscription.used')}</dt>
            <dd>{numberFormat.format(quota.used)}</dd>
          </div>
          <div>
            <dt>{t('organizationPage.subscription.remaining')}</dt>
            <dd>{numberFormat.format(quota.remaining)}</dd>
          </div>
          <div>
            <dt>{t('organizationPage.subscription.total')}</dt>
            <dd>{numberFormat.format(quota.limit)}</dd>
          </div>
        </dl>
      </>
    );
  };

  const percentage = quota && quota.limit > 0 ? (quota.used / quota.limit) * 100 : null;
  const showPercentage = !isLoading && !isError && !inactiveMessage && percentage !== null;

  return (
    <section aria-labelledby={headingId} className={classes.card}>
      <div className={classes.cardHeader}>
        <div className={classes.cardTitle}>
          <span aria-hidden="true" className={classes.icon}>
            <Icon />
          </span>
          <div className={classes.cardIntro}>
            <Heading id={headingId} level={2} data-size="xs">
              {t(`subscriptions.${kind}.pageTitle`)}
            </Heading>
            <Paragraph data-size="sm" className={classes.subtle}>
              {t(`organizationPage.subscription.${kind}.description`)}
            </Paragraph>
          </div>
        </div>
        {showPercentage && (
          <p className={classes.percentage}>
            <span className={classes.percentageValue}>
              {percentage > 0 && percentage < 1
                ? t('organizationPage.subscription.underOnePercent')
                : `${Math.round(percentage)} %`}
            </span>
            <span className={classes.subtle}>{t('organizationPage.subscription.usedLabel')}</span>
          </p>
        )}
      </div>
      {body()}
    </section>
  );
};

export const SubscriptionsPage = (): ReactElement => {
  const { t } = useTranslation();
  const { data: trialStatus, isError: trialError, isLoading: trialLoading } = useGetTrialStatus();
  const { data: smsQuota, isError: smsError, isLoading: smsLoading } = useGetSmsQuotaQuery();
  const { data: seatQuota, isError: seatError, isLoading: seatLoading } = useGetSeatQuotaQuery();

  const getTrialStatusText = (): string => {
    if (trialError) return t('subscriptions.overview.trial.errorStatus');
    if (trialLoading || !trialStatus) return t('subscriptions.overview.trial.loading');
    const { status, daysRemaining } = trialStatus;
    if (status === 'trial') {
      if (daysRemaining === null) return t('subscriptions.overview.trial.active');
      if (daysRemaining === 0) return t('subscriptions.overview.trial.expiresToday');
      if (daysRemaining === 1) return t('subscriptions.overview.trial.expiresInOneDay');
      return t('subscriptions.overview.trial.expiresInDays', { count: daysRemaining });
    }
    if (status === 'expired') return t('subscriptions.overview.trial.expired');
    return t('subscriptions.overview.trial.unknown');
  };

  const showTrial = trialStatus?.status === 'trial' || trialStatus?.status === 'expired';

  return (
    <OrganizationLayout>
      {showTrial && (
        <section aria-labelledby="trial-heading" className={classes.card} data-expired={trialStatus.isExpired}>
          <div className={classes.cardTitle}>
            <span aria-hidden="true" className={classes.icon}>
              <ClockDashedIcon />
            </span>
            <div className={classes.cardIntro}>
              <Heading id="trial-heading" level={2} data-size="xs">
                {t('subscriptions.overview.trial.title')}
              </Heading>
              <Paragraph>{getTrialStatusText()}</Paragraph>
            </div>
          </div>
          <dl className={classes.dates}>
            {trialStatus.trialStartDate && (
              <div>
                <dt>{t('subscriptions.overview.trial.startedLabel')}</dt>
                <dd>{formatNorwegianDate(trialStatus.trialStartDate)}</dd>
              </div>
            )}
            {trialStatus.trialEndDate && (
              <div>
                <dt>
                  {trialStatus.isExpired
                    ? t('subscriptions.overview.trial.expiredLabel')
                    : t('subscriptions.overview.trial.expiresLabel')}
                </dt>
                <dd>{formatNorwegianDate(trialStatus.trialEndDate)}</dd>
              </div>
            )}
          </dl>
          <div>
            <a href="https://trustpulse.no/kontakt" target="_blank" rel="noopener noreferrer" className="ds-link">
              {t('subscriptions.overview.trial.upgradeLink')}
            </a>
          </div>
        </section>
      )}

      <QuotaCard
        kind="seatQuota"
        icon={PersonGroupIcon}
        quota={seatQuota}
        isLoading={seatLoading}
        isError={seatError}
        inactiveMessage={seatQuota?.limit === QUOTA_DISABLED ? t('subscriptions.seatQuota.unlimited') : undefined}
      />
      <QuotaCard
        kind="smsQuota"
        icon={ChatElipsisIcon}
        quota={smsQuota}
        isLoading={smsLoading}
        isError={smsError}
        inactiveMessage={
          smsQuota && (smsQuota.limit === QUOTA_DISABLED || !smsQuota.allowed)
            ? t('subscriptions.smsQuota.disabled')
            : undefined
        }
      />
    </OrganizationLayout>
  );
};
